import { useCallback, useMemo, useState } from "react";
import { GiftVoucherStatus } from "../../../../common/enums";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { IGiftVoucherModel } from "../models/giftVoucherModels";
import { useAppContext } from "../../../../AppContext";

/**
 * Page logic for the gift-voucher list: loading, the open dialog, and the money totals
 * shown at the top.
 *
 * The API already returns the list in the order the page wants (unsold first, then sold
 * and still carrying money, then finished) so nothing is re-sorted here — a second sort
 * on the client would silently drift from what the server considers current.
 */
export const useGiftVouchersPage = () => {
  const { isMobile } = useAppContext();
  const [isFormDialogVisible, setIsFormDialogVisible] = useState(false);
  const [isRedeemDialogVisible, setIsRedeemDialogVisible] = useState(false);
  const [editedVoucher, setEditedVoucher] = useState<IGiftVoucherModel | null>(
    null,
  );
  const [selectedVoucherId, setSelectedVoucherId] = useState<number | null>(
    null,
  );

  const { data: vouchers, isLoading } = useApiQuery<IGiftVoucherModel[]>({
    queryKey: [endpointsMetadata.giftVoucher.getAll],
    endpoint: endpointsMetadata.giftVoucher.getAll,
    // The list shows its own skeleton rows; the full-screen loader on top of them
    // would only hide that.
    globalIsLoading: false,
  });

  const { mutate, isLoading: isTogglingDeletion } = useApiMutation();

  /**
   * What the studio currently owes on vouchers: the sum of every LIVE balance. This is
   * the liability figure, not turnover — the cash for it is already in the drawer and the
   * revenue is recognized only as the vouchers get spent.
   */
  const outstandingLiability = useMemo(
    () =>
      (vouchers ?? [])
        .filter((voucher) => !voucher.isDeleted && !voucher.isExpired)
        .reduce((total, voucher) => total + voucher.remainingAmount, 0),
    [vouchers],
  );

  /** Vouchers printed and waiting on the counter to be sold. */
  const unsoldCount = useMemo(
    () =>
      (vouchers ?? []).filter(
        (voucher) =>
          !voucher.isDeleted &&
          !voucher.isExpired &&
          voucher.status === GiftVoucherStatus.New,
      ).length,
    [vouchers],
  );

  const openCreateDialog = useCallback(() => {
    setEditedVoucher(null);
    setIsFormDialogVisible(true);
  }, []);

  const openEditDialog = useCallback((voucher: IGiftVoucherModel) => {
    setEditedVoucher(voucher);
    setIsFormDialogVisible(true);
  }, []);

  const closeFormDialog = useCallback(() => {
    setIsFormDialogVisible(false);
    setEditedVoucher(null);
  }, []);

  const toggleDeletion = useCallback(
    (voucher: IGiftVoucherModel) => {
      mutate({
        endpoint: `${endpointsMetadata.giftVoucher.toggleDeletionStatus}?id=${voucher.id}`,
        method: "post",
        globalIsLoading: true,
        successMessage: voucher.isDeleted
          ? "Ваучерът е възстановен."
          : "Ваучерът е изтрит.",
        invalidateKeys: [[endpointsMetadata.giftVoucher.getAll]],
      });
    },
    [mutate],
  );

  return {
    // On a phone the two page buttons do not fit next to the title, so the page moves
    // them into a row under it.
    isMobile,
    vouchers,
    isLoading,
    isTogglingDeletion,
    outstandingLiability,
    unsoldCount,
    isFormDialogVisible,
    isRedeemDialogVisible,
    editedVoucher,
    selectedVoucherId,
    openCreateDialog,
    openEditDialog,
    closeFormDialog,
    openRedeemDialog: () => setIsRedeemDialogVisible(true),
    closeRedeemDialog: () => setIsRedeemDialogVisible(false),
    openDetails: (voucherId: number) => setSelectedVoucherId(voucherId),
    closeDetails: () => setSelectedVoucherId(null),
    toggleDeletion,
  };
};
